import React, { useId } from "react";

import { isValidHexColor, normalizeHexColor } from "@performyard/utils-isomorphic";

type HexInputProps = {
	value: string;
	onChange: (next: string) => void;
	disabled?: boolean;
	"data-testid"?: string;
};

/** Hex text field with a clickable swatch that opens the native color picker. */
export const HexInput = ({ value, onChange, disabled, "data-testid": dataTestId }: HexInputProps) => {
	const inputId = useId();
	const colorInputId = `${inputId}-color`;
	const isValid = isValidHexColor(value);
	const swatchColor = isValid ? normalizeHexColor(value) : "transparent";

	return (
		<div className="flex items-center gap-2">
			<label
				htmlFor={colorInputId}
				className="inline-block h-[26px] w-[26px] shrink-0 cursor-pointer rounded-[2px] border border-solid border-neutral-3"
				style={{ backgroundColor: swatchColor }}
				aria-label="Open color picker"
			/>
			<input
				id={colorInputId}
				type="color"
				className="sr-only"
				value={isValid ? swatchColor : "#000000"}
				onChange={(event) => onChange(event.target.value.toUpperCase())}
				disabled={disabled}
			/>
			<input
				id={inputId}
				type="text"
				className="block w-full appearance-none rounded-md border border-solid border-neutral-3 bg-neutral-0 px-2 py-1.5 text-paragraph-3 text-neutral-9 placeholder:text-neutral-5 focus:outline-none disabled:opacity-50"
				value={value}
				onChange={(event) => onChange(event.target.value)}
				placeholder="#FFFFFF"
				maxLength={7}
				spellCheck={false}
				disabled={disabled}
				data-testid={dataTestId}
			/>
		</div>
	);
};
